import { get } from './API';

/**
 * The correspondence archive — ONE endpoint, four readings of it.
 *
 * READ-ONLY, and there is nothing to add: `GET /email-inbox` is the whole
 * surface. Nothing here drafts, suggests or sends, and the customer-service
 * viewer over it is a reader. A reply is still written in a mail client.
 *
 * ── THE VOCABULARY IS CONVERSATIONS AND MESSAGES, NOT EMAILS ──
 * `listConversations` and `getConversation` answer in a channel-neutral shape
 * — a conversation of messages with a `direction`, `participants`, a
 * `channel` and the `account` it arrived on — because WhatsApp is intended to
 * arrive here later and a screen is the expensive half to change. Everything
 * true only of mail is nested under a message's `email` key. A second channel
 * should be a new producer of these shapes plus a filter chip, not a rewrite.
 *
 * The two older readings (`listEmails`, `getEmail`) stay flat and email-shaped
 * because that is what they are: the inbox browser the AI extract panels pick
 * a lead out of. `getEmail` is also how a conversation message's BODY is
 * fetched — a message's `id` IS the key it is read by.
 *
 * ── EVERY METHOD RETURNS ITS RESULT ALREADY UNPACKED ──
 * The API wraps a handler's return as `{ success, data }` unless it already
 * carries a `data` key (SuccessResponseMiddleware), so every mode of this
 * endpoint lands at `response.data.data`. Unwrapping it here, with a default
 * for each field, is what lets a reset jest mock answering `undefined` render
 * an empty screen instead of throwing.
 */

/** `response.data.data`, with the one legacy nesting tolerated. */
const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

/**
 * THREE STATES, AND THE THIRD IS REAL.
 *
 * `unknown` is not a tick and not a blank: conversations indexed before
 * October carry no threading headers and genuinely cannot be judged. Rendered
 * as either of the other two it would read as "nothing is wrong", and the
 * false "waiting" is worse still — it sends the desk to chase a customer who
 * was already answered.
 *
 * `tone` is the badge's colour and the rule the design states outright: AMBER
 * IS A HUMAN IS NEEDED. `awaiting` is the only amber of the three, which is
 * also why the age beside it is the loudest thing on a row — the business
 * problem this screen exists for is 7–22 hour weekend response times.
 */
export const REPLIED_STATES = [
  { value: 'awaiting', label: 'Awaiting reply', tone: 'warn' },
  { value: 'replied', label: 'Replied', tone: 'good' },
  { value: 'unknown', label: 'Unknown', tone: 'muted' },
];

/** The state the "Awaiting" segment filters to: the queue wanting a human. */
export const AWAITING_STATE = 'awaiting';

/**
 * Why the record cannot say, in the desk's words rather than the server's.
 *
 * Shown WITH the `unknown` badge, never instead of it. "We could not tell"
 * that does not say why is indistinguishable from a bug in this screen.
 */
export const UNKNOWN_REASONS = {
  not_linked: 'no threading headers were captured on this message',
  beyond_resolution: 'older than the replied-state check reached',
};

/** Why the mailbox cannot be named. Same rule: shown, never swallowed. */
export const ACCOUNT_UNKNOWN_REASONS = {
  not_captured: 'recipients were not recorded on these messages',
  not_addressed_to_us: 'none of the recipients is one of our mailboxes',
};

/** A descriptor by value, so a state the server adds still renders. */
export const repliedDescriptor = (value) => (
  REPLIED_STATES.find((entry) => entry.value === value)
  || (value ? { value, label: value, tone: 'muted' } : null)
);

/** A reason the server adds reads as itself rather than disappearing. */
export const reasonLabel = (reasons, value) => (value ? reasons[value] || value : '');

/**
 * `email: info@pickuphungary.com` — WHERE A CONVERSATION ARRIVED.
 *
 * On every queue row and in the thread header, which is why it is a function
 * and not written twice: a second copy would be the one that starts saying
 * something different about a mailbox the record cannot name.
 *
 * THE CHANNEL IS NAMED EVEN THOUGH THERE IS ONLY ONE TODAY. More mailboxes
 * and more channels are coming and the queue is mixed the moment they land; a
 * reader who has never seen the channel named cannot tell a WhatsApp from a
 * mail at a glance, and by then the habit of not looking is formed.
 *
 * The mailbox is NOT defaulted when it is missing — see
 * `ACCOUNT_UNKNOWN_REASONS` — and the caller with room for a sentence adds
 * the reason beside this.
 */
export const channelAccountLabel = (conversation) => {
  const channel = conversation?.channel || 'email';
  return conversation?.account
    ? `${channel}: ${conversation.account}`
    : `${channel}: mailbox unknown`;
};

/**
 * WHICH SERVER FILTER A SINGLE SEARCH BOX MEANS.
 *
 * The endpoint has three separate sender/subject filters and they do NOT
 * match alike: `fromEmail` is an EXACT equality on the From address,
 * `fromDomain` is a true suffix match, and `subjectContains` is a substring.
 * One box therefore has to choose, and choosing wrong is silent — a partial
 * address routed to the exact filter returns nothing at all rather than
 * "no match for what you meant".
 *
 * So the shape of what was typed decides, and the screen SAYS which filter it
 * applied (see `searchHint`). Explicit over clever: an agent who sees "sender
 * is exactly …" and gets nothing knows to type less, which they cannot guess
 * from an empty list.
 */
export const searchFilter = (term) => {
  const text = String(term ?? '').trim();
  if (!text) return {};
  if (text.startsWith('@')) return { fromDomain: text.slice(1) };
  if (text.includes('@')) return { fromEmail: text };
  return { subjectContains: text };
};

/** What the screen prints next to the box, so the match rule is never hidden. */
export const searchHint = (term) => {
  const filter = searchFilter(term);
  if (filter.fromDomain) return `sender domain ends with ${filter.fromDomain}`;
  if (filter.fromEmail) return `sender is exactly ${filter.fromEmail}`;
  if (filter.subjectContains) return `subject contains ${filter.subjectContains}`;
  return '';
};

/** Empty values dropped, so a cleared filter is absent rather than `?replied=`. */
const compact = (params) => Object.fromEntries(
  Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
);

const EmailInboxService = {
  /**
   * One page of CONVERSATIONS, newest matching message first.
   *
   * `filters` takes `replied` (awaiting | replied | unknown), `account`,
   * `dateFrom`, `dateTo`, `fromEmail`, `fromDomain`, `subjectContains`,
   * `cursor` and `limit` (the server caps it at 100).
   *
   * NO `account` IS SENT UNLESS ASKED FOR. A conversation whose mailbox the
   * record cannot name is not in a filtered list, and that is every
   * conversation older than 2026-10-12 — so defaulting one would read as an
   * empty archive rather than as the desk's own mailbox.
   *
   * `resolution` describes how far back the replied-state answers are exact.
   * It is returned rather than dropped because a filter is only worth having
   * if the screen can say what it covers.
   */
  listConversations: async (filters = {}) => {
    const response = await get('/email-inbox', compact({ ...filters, view: 'conversations' }));
    const body = unwrap(response);
    return {
      conversations: body.conversations || [],
      cursor: body.cursor || null,
      resolution: body.resolution || { from: null, scanned: 0, truncated: false },
    };
  },

  /** One conversation and its messages, OLDEST FIRST — the order it happened. */
  getConversation: async (id) => {
    const response = await get('/email-inbox', { conversation: id });
    const body = unwrap(response);
    if (!body.id) return null;
    return {
      ...body,
      messages: body.messages || [],
      participants: body.participants || [],
      truncated: !!body.truncated,
    };
  },

  /** The flat, email-shaped inbox browser the AI extract panels read. */
  listEmails: async (params = {}) => {
    const body = unwrap(await get('/email-inbox', compact(params)));
    return { emails: body.emails || [], cursor: body.cursor || null };
  },

  /**
   * One message's stored content, by the key its `id` is.
   *
   * `source` is worth rendering: `sanitized` is the KB's reduced copy, read
   * when the `.eml` has aged into Glacier, and it keeps NO html — so a body
   * that falls back to `text` is not a rendering bug.
   */
  getEmail: async (key) => {
    const body = unwrap(await get('/email-inbox', { key }));
    return {
      ...body, html: body.html || '', text: body.text || '', source: body.source || null,
    };
  },
};

export default EmailInboxService;
